import React, { createContext, useContext, ReactNode } from 'react';

interface ThemeContextType {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  fonts: {
    primary: string;
    secondary: string;
  };
  logo?: {
    url: string;
    alt: string;
  };
}

interface ThemeProviderProps {
  children: ReactNode;
  theme: ThemeContextType;
}

const defaultTheme: ThemeContextType = {
  colors: {
    primary: '#c79043',
    secondary: '#6c757d',
    accent: '#28a745',
    background: '#ffffff',
    text: '#212529'
  },
  fonts: {
    primary: 'Inter, system-ui, sans-serif',
    secondary: 'system-ui, sans-serif'
  },
  logo: {
    url: 'https://app.speculo.ai/speculo.ai-black.png',
    alt: 'Speculo AI'
  }
};

const ThemeContext = createContext<ThemeContextType>(defaultTheme);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, theme }) => {
  // Merge provided theme with default theme
  const mergedTheme = {
    ...defaultTheme,
    ...theme,
    colors: {
      ...defaultTheme.colors,
      ...theme.colors
    },
    fonts: {
      ...defaultTheme.fonts,
      ...theme.fonts
    }
  };

  // Apply theme variables to :root
  React.useEffect(() => {
    const root = document.documentElement;
    Object.entries(mergedTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    Object.entries(mergedTheme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--font-${key}`, value);
    });
  }, [mergedTheme]);

  return (
    <ThemeContext.Provider value={mergedTheme}>
      {children}
    </ThemeContext.Provider>
  );
};

export type { ThemeContextType };
